import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ErrorLog, ErrorLogs } from "../../api/observeApi";
import { fetchErrorLogs } from "./errorLogThunks";
import { ErrorLogSummary, LoadingStatus } from "../../common/types";

export interface ErrorLogState {
  logs: ErrorLog[];
  totalLogs: number;
  offset: number;
  resultsPerPage: number;
  status: LoadingStatus;
  summary: ErrorLogSummary;
  summaryStatus: LoadingStatus;
  filters: string;
  query: string;
  timeInterval: number;
  currentTime: number;
}

const initialState: ErrorLogState = {
  logs: [],
  totalLogs: 0,
  offset: 0,
  resultsPerPage: 100,
  status: "idle",
  summaryStatus: "idle",
  summary: {
    error_count: 0,
    calculated_at: 0,
    duration_text: "",
  },
  filters: "",
  query: "",
  timeInterval: 60 * 60, // One hour
  currentTime: Date.now() * 1000000, // in nanoseconds
};

export const errorLogSlice = createSlice({
  name: "errorLog",
  initialState,
  reducers: {
    setStatus: (state, action: PayloadAction<LoadingStatus>) => {
      state.status = action.payload;
    },
    setOffset: (state, action: PayloadAction<number>) => {
      state.offset = action.payload;
    },
    resetLogs: (state) => {
      state.logs = [];
      state.currentTime = Date.now() * 1000000;
    },
    resetErrorLogState: () => initialState,
    setFilters: (state, action: PayloadAction<string>) => {
      state.logs = [];
      state.currentTime = Date.now() * 1000000;
      state.filters = action.payload;
    },
    setQuery: (state, action: PayloadAction<string>) => {
      state.logs = [];
      state.currentTime = Date.now() * 1000000;
      state.query = action.payload;
    },
    setTimeInterval: (state, action: PayloadAction<number>) => {
      state.timeInterval = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchErrorLogs.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchErrorLogs.fulfilled, (state, action) => {
        state.status = "idle";
        const resp = action.payload as ErrorLogs;
        if (resp.results) {
          state.logs.push(...resp.results);
        }
        if (resp.last_row_time) {
          state.currentTime = resp.last_row_time;
        }
        state.totalLogs = 1000000;
        if (!resp.results || resp.results?.length === 0) {
          state.totalLogs = state.logs.length;
        }
      })
      .addCase(fetchErrorLogs.rejected, (state, action) => {
        state.status = "failed";
        console.log("failed", action);
      });
  },
});

export const {
  setStatus,
  setOffset,
  resetLogs,
  resetErrorLogState,
  setFilters,
  setQuery,
  setTimeInterval,
} = errorLogSlice.actions;

export default errorLogSlice.reducer;
